import {useMutation} from '@apollo/react-hooks';

import type {User} from 'nutshell-core/users/users-types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    AddAttachmentToAccount as AddAttachmentToAccountMutation,
    AddAttachmentToAccountVariables,
    AccountFileAddInput,
    FileFragment,
} from 'nutshell-graphql-types';

import {getFileType} from '../../../../../../core-entity/widgets/attachments-widget/helpers';

import ADD_ATTACHMENT_TO_ACCOUNT from '../mutations/add-attachment-to-account.graphql';

export const useAddAttachmentToAccount = (): {
    addAttachmentToAccount: (
        arg1: AccountFileAddInput,
        arg2?: User | null | undefined,
        arg3?: FileFragment[] | null | undefined
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [addAttachmentToAccount, {loading, error}] = useMutation<
        AddAttachmentToAccountMutation,
        AddAttachmentToAccountVariables
    >(ADD_ATTACHMENT_TO_ACCOUNT);

    return {
        addAttachmentToAccount: (
            input: AccountFileAddInput,
            user?: User | null,
            existingFiles?: FileFragment[] | null
        ) => {
            let mutationReturn = null;

            if (user && existingFiles) {
                mutationReturn = addAttachmentToAccount({
                    variables: {input},
                    // @ts-expect-error
                    optimisticResponse: () => {
                        const newFiles = input.files.map((file) => ({
                            __typename: 'EntityAttributeValueFile',
                            fileId: String(Date.now()) + String(Math.random()),
                            href: file.locationUri,
                            viewHref: file.locationUri,
                            viewableInBrowser: false,
                            fileType: getFileType(file.mimeType, file.filename),
                            uploadedTime: Math.floor(Date.now() / 1000),
                            name: file.filename,
                            creatorUser: {
                                __typename: 'User',
                                id: user.id,
                                name: user.name,
                                avatarUrl: user.avatarUrl,
                                initials: user.initials,
                            },
                        }));

                        return {
                            accountFilesAdd: {
                                __typename: 'AccountFileAddPayload',
                                account: {
                                    __typename: 'Account',
                                    id: input.account,
                                    files: [...newFiles.reverse(), ...existingFiles],
                                },
                            },
                        };
                    },
                });
            } else {
                mutationReturn = addAttachmentToAccount({
                    variables: {input},
                });
            }

            return mutationReturn;
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
